import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { registerSW } from './registerSW';
import { initPush } from './lib/initPush';
import { ALLOWED_EXAM_TAGS, ANNOUNCEMENT_CATEGORIES, examTagLabel, categoryLabel } from './lib/examTags';
// Eagerly loaded: only the landing route itself. Every OTHER public entry point is lazy —
// a visitor on `/` pays for exactly one route, and a visitor on `/updates` pays for that
// one. Keeping /updates, /m/* and /mentor-signup eager cost the landing page their JS on
// the critical path for no benefit: crawlers get prerendered HTML for those routes anyway
// (see the hosting rewrites in firebase.json), and the Suspense fallback below covers the
// one-chunk fetch for humans.
import { Home } from './routes/Home';
import { Healthz } from './routes/Healthz';
import { RouteMeta } from './components/RouteMeta';
import { NotFound } from './routes/NotFound';

// Lazy route components (named exports → unwrap to { default }). These split into separate
// chunks fetched only when their route is visited — they're auth/dashboard/admin/learner/
// legal pages that public and social traffic never load.
// AdminGate calls auth(), so it MUST be lazy too — otherwise (being used inline in the admin
// route elements) it pulls firebase-auth into the eager public bundle.
const MentorRoute = lazy(() => import('./routes/MentorSeoLanding').then((m) => ({ default: m.MentorRoute })));
const MentorSignup = lazy(() => import('./routes/MentorSignup').then((m) => ({ default: m.MentorSignup })));
const Updates = lazy(() => import('./routes/Updates').then((m) => ({ default: m.Updates })));
const UpdateDetail = lazy(() => import('./routes/UpdateDetail').then((m) => ({ default: m.UpdateDetail })));
const ExamCalendar = lazy(() => import('./routes/ExamCalendar').then((m) => ({ default: m.ExamCalendar })));
const ExamCalendarHub = lazy(() => import('./routes/ExamCalendar').then((m) => ({ default: m.ExamCalendarHub })));
const AdminGate = lazy(() => import('./routes/AdminGate').then((m) => ({ default: m.AdminGate })));
const Signup = lazy(() => import('./routes/Signup').then((m) => ({ default: m.Signup })));
const StudyGuidesIndex = lazy(() => import('./routes/StudyGuidesIndex').then((m) => ({ default: m.StudyGuidesIndex })));
const StudyGuide = lazy(() => import('./routes/StudyGuide').then((m) => ({ default: m.StudyGuide })));
const AdminStudyGuides = lazy(() => import('./routes/AdminStudyGuides').then((m) => ({ default: m.AdminStudyGuides })));
const AdminSocial = lazy(() => import('./routes/AdminSocial').then((m) => ({ default: m.AdminSocial })));
const About = lazy(() => import('./routes/About').then((m) => ({ default: m.About })));
const Contact = lazy(() => import('./routes/Contact').then((m) => ({ default: m.Contact })));
const Dashboard = lazy(() => import('./routes/Dashboard').then((m) => ({ default: m.Dashboard })));
const ProfileSetup = lazy(() => import('./routes/ProfileSetup').then((m) => ({ default: m.ProfileSetup })));
const ChangeLoginPhone = lazy(() => import('./routes/ChangeLoginPhone').then((m) => ({ default: m.ChangeLoginPhone })));
const SubscriptionSetup = lazy(() => import('./routes/SubscriptionSetup').then((m) => ({ default: m.SubscriptionSetup })));
const LeadsInbox = lazy(() => import('./routes/LeadsInbox').then((m) => ({ default: m.LeadsInbox })));
const BookingsInbox = lazy(() => import('./routes/BookingsInbox').then((m) => ({ default: m.BookingsInbox })));
const ReviewsInbox = lazy(() => import('./routes/ReviewsInbox').then((m) => ({ default: m.ReviewsInbox })));
const AdminHome = lazy(() => import('./routes/AdminHome').then((m) => ({ default: m.AdminHome })));
const AdminMentorEdit = lazy(() => import('./routes/AdminMentorEdit').then((m) => ({ default: m.AdminMentorEdit })));
const ComingSoon = lazy(() => import('./routes/ComingSoon').then((m) => ({ default: m.ComingSoon })));
const PrivacyPolicy = lazy(() => import('./routes/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })));
const Terms = lazy(() => import('./routes/Terms').then((m) => ({ default: m.Terms })));
const ReviewConfirm = lazy(() => import('./routes/ReviewConfirm').then((m) => ({ default: m.ReviewConfirm })));
const Kyc = lazy(() => import('./routes/Kyc').then((m) => ({ default: m.Kyc })));
const AdminKyc = lazy(() => import('./routes/AdminKyc').then((m) => ({ default: m.AdminKyc })));
const CoursesList = lazy(() => import('./routes/CoursesList').then((m) => ({ default: m.CoursesList })));
const CourseNew = lazy(() => import('./routes/CourseNew').then((m) => ({ default: m.CourseNew })));
const LearnerSignup = lazy(() => import('./routes/LearnerSignup').then((m) => ({ default: m.LearnerSignup })));
const LearnerDashboard = lazy(() => import('./routes/LearnerDashboard').then((m) => ({ default: m.LearnerDashboard })));
const Browse = lazy(() => import('./routes/Browse').then((m) => ({ default: m.Browse })));
const LearnerCourseDetail = lazy(() => import('./routes/LearnerCourseDetail').then((m) => ({ default: m.LearnerCourseDetail })));
const UpdatesUnsubscribe = lazy(() => import('./routes/UpdatesUnsubscribe').then((m) => ({ default: m.UpdatesUnsubscribe })));
const AdminAnnouncements = lazy(() => import('./routes/AdminAnnouncements').then((m) => ({ default: m.AdminAnnouncements })));
const AdminAnnouncementEdit = lazy(() => import('./routes/AdminAnnouncementEdit').then((m) => ({ default: m.AdminAnnouncementEdit })));
const QuestionPapers = lazy(() => import('./routes/QuestionPapers').then((m) => ({ default: m.QuestionPapers })));
const TeachingJobs = lazy(() => import('./routes/TeachingJobs').then((m) => ({ default: m.TeachingJobs })));
const QuestionPaperDetail = lazy(() => import('./routes/QuestionPaperDetail').then((m) => ({ default: m.QuestionPaperDetail })));
const StrategyGuides = lazy(() => import('./routes/StrategyGuides').then((m) => ({ default: m.StrategyGuides })));
const SuccessStories = lazy(() => import('./routes/SuccessStories').then((m) => ({ default: m.SuccessStories })));
const ExamBuddyHub = lazy(() => import('./routes/examBuddy/ExamBuddyHub').then((m) => ({ default: m.ExamBuddyHub })));
const CareerFitScorecard = lazy(() => import('./routes/examBuddy/CareerFitScorecard').then((m) => ({ default: m.CareerFitScorecard })));
const StreamGuidance = lazy(() => import('./routes/examBuddy/StreamGuidance').then((m) => ({ default: m.StreamGuidance })));
const CollegeFinder = lazy(() => import('./routes/examBuddy/CollegeFinder').then((m) => ({ default: m.CollegeFinder })));
const CareerDoors = lazy(() => import('./routes/examBuddy/CareerDoors').then((m) => ({ default: m.CareerDoors })));
const AstroAdvice = lazy(() => import('./routes/examBuddy/AstroAdvice').then((m) => ({ default: m.AstroAdvice })));
const AdminQuestionPapers = lazy(() => import('./routes/AdminQuestionPapers').then((m) => ({ default: m.AdminQuestionPapers })));
const AdminNewMembers = lazy(() => import('./routes/AdminNewMembers').then((m) => ({ default: m.AdminNewMembers })));
const AdminFederation = lazy(() => import('./routes/AdminFederation').then((m) => ({ default: m.AdminFederation })));
const AdminExamBuddy = lazy(() => import('./routes/AdminExamBuddy').then((m) => ({ default: m.AdminExamBuddy })));
const AdminAstroAdvice = lazy(() => import('./routes/AdminAstroAdvice').then((m) => ({ default: m.AdminAstroAdvice })));
const AdminBlockedNumbers = lazy(() => import('./routes/AdminBlockedNumbers').then((m) => ({ default: m.AdminBlockedNumbers })));
const AdminCategories = lazy(() => import('./routes/AdminCategories').then((m) => ({ default: m.AdminCategories })));
const Chat = lazy(() => import('./routes/Chat').then((m) => ({ default: m.Chat })));
const ChatThread = lazy(() => import('./routes/ChatThread').then((m) => ({ default: m.ChatThread })));
const AdminChatModeration = lazy(() => import('./routes/AdminChatModeration').then((m) => ({ default: m.AdminChatModeration })));
const KunjeansPillar = lazy(() => import('./routes/KunjeansPillar').then((m) => ({ default: m.KunjeansPillar })));
const KunjeansDetail = lazy(() => import('./routes/KunjeansDetail').then((m) => ({ default: m.KunjeansDetail })));
const ShareDiary = lazy(() => import('./routes/ShareDiary').then((m) => ({ default: m.ShareDiary })));
const ShareAchievement = lazy(() => import('./routes/ShareAchievement').then((m) => ({ default: m.ShareAchievement })));
const AdminKunjeanStories = lazy(() => import('./routes/AdminKunjeanStories').then((m) => ({ default: m.AdminKunjeanStories })));

// /updates/{exam|category|source}/<value> SEO landing pages — render the Updates hub locked
// to one filter dimension. Validates the value against the allow-lists; unknown → /updates.
// (The prerender owns the crawler-facing <head> + body; this is the hydrated client view.)
function UpdatesLanding({ kind }: { kind: 'exam' | 'category' | 'source' }) {
  const v = (useParams().value ?? '').toLowerCase();
  if (kind === 'exam' && !(ALLOWED_EXAM_TAGS as readonly string[]).includes(v)) return <Navigate to="/updates" replace />;
  if (kind === 'category' && (!(ANNOUNCEMENT_CATEGORIES as readonly string[]).includes(v) || v === 'general' || v === 'other')) return <Navigate to="/updates" replace />;
  if (!v) return <Navigate to="/updates" replace />;
  const label = kind === 'exam' ? examTagLabel(v)
    : kind === 'category' ? categoryLabel(v)
    : v.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return <Updates lock={{ kind, value: v, label }} />;
}

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

registerSW(); // PWA service worker (production only)
initPush();   // native push (no-op on web; active only inside the Capacitor app)

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <AppLayout>
      <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontFamily: 'system-ui, sans-serif' }}>Loading…</div>}>
      <ErrorBoundary>
      {/* Canonical + robots for client-rendered routes; see components/RouteMeta.tsx. */}
      <RouteMeta />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/handbook" element={<Home />} />
        <Route path="/health" element={<Healthz />} />
        <Route path="/exam-calendar" element={<ExamCalendarHub />} />
        <Route path="/exam-calendar/:slug" element={<ExamCalendar />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/mentor-signup" element={<MentorSignup />} />

        {/* Phase 1 routes */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/profile" element={<ProfileSetup />} />
        <Route path="/dashboard/change-phone" element={<ChangeLoginPhone />} />
        <Route path="/dashboard/subscription" element={<SubscriptionSetup />} />
        <Route path="/dashboard/leads" element={<LeadsInbox />} />
        <Route path="/dashboard/bookings" element={<BookingsInbox />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:chatId" element={<ChatThread />} />
        <Route path="/admin/chat-moderation" element={<AdminGate><AdminChatModeration /></AdminGate>} />
        <Route path="/dashboard/reviews" element={<ReviewsInbox />} />
        <Route path="/admin" element={<AdminGate><AdminHome /></AdminGate>} />
        <Route path="/admin/mentor/:mentorId" element={<AdminGate><AdminMentorEdit /></AdminGate>} />
        <Route path="/admin/announcements" element={<AdminGate><AdminAnnouncements /></AdminGate>} />
        <Route path="/admin/announcements/new" element={<AdminGate><AdminAnnouncementEdit /></AdminGate>} />
        <Route path="/admin/announcements/edit/:id" element={<AdminGate><AdminAnnouncementEdit /></AdminGate>} />

        {/* Phase 2 routes — accessible by URL, not linked from UI */}
        <Route path="/dashboard/kyc" element={<Kyc />} />
        <Route path="/dashboard/courses" element={<CoursesList />} />
        <Route path="/dashboard/courses/new" element={<CourseNew />} />
        <Route path="/admin/kyc" element={<AdminKyc />} />
        <Route path="/learner/signup" element={<LearnerSignup />} />
        <Route path="/learner" element={<LearnerDashboard />} />
        <Route path="/learner/browse" element={<Browse />} />
        <Route path="/learner/c/:slug/:courseSlug" element={<LearnerCourseDetail />} />

        {/* Updates (announcements) content layer. Static segments rank above the
            dynamic :slug in react-router v6; declared explicitly for clarity. */}
        <Route path="/study-guides" element={<StudyGuidesIndex />} />
        <Route path="/study-guides/:examSlug/:year" element={<StudyGuide />} />
        <Route path="/admin/study-guides" element={<AdminGate><AdminStudyGuides /></AdminGate>} />

        <Route path="/question-papers" element={<QuestionPapers />} />
        <Route path="/question-papers/:id" element={<QuestionPaperDetail />} />
        <Route path="/teaching-jobs" element={<TeachingJobs />} />
        <Route path="/strategy-guides" element={<StrategyGuides />} />
        <Route path="/success-stories" element={<SuccessStories />} />
        <Route path="/exam-buddy" element={<ExamBuddyHub />} />
        <Route path="/exam-buddy/career-fit-scorecard" element={<CareerFitScorecard />} />
        <Route path="/exam-buddy/stream-guidance" element={<StreamGuidance />} />
        <Route path="/exam-buddy/college-finder" element={<CollegeFinder />} />
        <Route path="/exam-buddy/career-doors" element={<CareerDoors />} />
        <Route path="/exam-buddy/astro-advice" element={<AstroAdvice />} />
        <Route path="/admin/question-papers" element={<AdminGate><AdminQuestionPapers /></AdminGate>} />
        <Route path="/admin/new-members" element={<AdminGate><AdminNewMembers /></AdminGate>} />
        <Route path="/admin/social" element={<AdminGate><AdminSocial /></AdminGate>} />
        <Route path="/admin/federation" element={<AdminGate><AdminFederation /></AdminGate>} />
        <Route path="/admin/exam-buddy" element={<AdminGate><AdminExamBuddy /></AdminGate>} />
        <Route path="/admin/astro-advice" element={<AdminGate><AdminAstroAdvice /></AdminGate>} />
        <Route path="/admin/blocked-numbers" element={<AdminGate><AdminBlockedNumbers /></AdminGate>} />
        <Route path="/admin/categories" element={<AdminGate><AdminCategories /></AdminGate>} />
        <Route path="/kunjeans" element={<KunjeansPillar />} />
        <Route path="/kunjeans/share-diary" element={<ShareDiary />} />
        <Route path="/kunjeans/share-achievement" element={<ShareAchievement />} />
        <Route path="/kunjeans/:slug" element={<KunjeansDetail />} />
        <Route path="/admin/kunjeans" element={<AdminGate><AdminKunjeanStories /></AdminGate>} />

        <Route path="/updates" element={<Updates />} />
        <Route path="/updates/exam/:value" element={<UpdatesLanding kind="exam" />} />
        <Route path="/updates/category/:value" element={<UpdatesLanding kind="category" />} />
        <Route path="/updates/source/:value" element={<UpdatesLanding kind="source" />} />
        <Route path="/updates/unsubscribe" element={<UpdatesUnsubscribe />} />
        <Route path="/updates/:slug" element={<UpdateDetail />} />

        <Route path="/m/:slug" element={<MentorRoute />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/r/confirm" element={<ReviewConfirm />} />
        <Route path="/m/:slug/c/:courseSlug" element={<ComingSoon />} />
        {/* Catch-all: without this, an unmatched URL rendered a blank 200 — a soft 404. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </ErrorBoundary>
      </Suspense>
      </AppLayout>
    </BrowserRouter>
  </React.StrictMode>,
);
